/**
 * Tutorial catalog grid: fetch, loading, empty state, and cards.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clapperboard } from 'lucide-react';
import { TutorialCard } from '@/components/tutorials/TutorialCard';
import { tutorialKeys } from '@/components/tutorials/tutorial-configs';
import {
  canEditTutorialRows,
  tutorialCatalogMode,
  tutorialCatalogRows,
  tutorialRowsVisibleInMode,
} from '@/components/tutorials/tutorial-grid-utils';
import { Spinner } from '@/components/ui/spinner';
import { getTutorialVideos, getTutorialVideosAdmin } from '@/services/tutorials.service';
import type { AppMode } from '@/contexts/ModeContext';
import type { TutorialVideo } from '@/types/tutorial.types';

/** Props for {@link TutorialsGrid}. */
type TutorialsGridProps = Readonly<{
  mode: AppMode | null;
  canEdit: boolean;
  canDelete: boolean;
  onWatch: (video: TutorialVideo) => void;
  onEdit: (id: string) => void;
  onDelete: (video: TutorialVideo) => void;
}>;

/** Loading spinner while the catalog query is pending. */
function TutorialsGridLoading(): React.ReactElement {
  return (
    <div className="flex justify-center p-8">
      <Spinner size="lg" label="Chargement..." />
    </div>
  );
}

/** Empty catalog state when there are no tutorial rows. */
function TutorialsGridEmpty(): React.ReactElement {
  return (
    <div className="table-empty">
      <Clapperboard className="table-empty-icon" />
      <p className="table-empty-text">Aucun tutoriel pour le moment.</p>
    </div>
  );
}

/** Props for the catalog card list. */
type TutorialsGridCardsProps = Readonly<{
  videos: TutorialVideo[];
  canEdit: boolean;
  canDelete: boolean;
  onWatch: (video: TutorialVideo) => void;
  onEdit: (id: string) => void;
  onDelete: (video: TutorialVideo) => void;
}>;

/** Responsive card grid for catalog rows. */
function TutorialsGridCards({
  videos,
  canEdit,
  canDelete,
  onWatch,
  onEdit,
  onDelete,
}: TutorialsGridCardsProps): React.ReactElement {
  function renderVideoCard(video: TutorialVideo): React.ReactNode {
    return (
      <TutorialCard
        key={video.id}
        video={video}
        canEdit={canEdit}
        canDelete={canDelete}
        onWatch={onWatch}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {videos.map(renderVideoCard)}
    </div>
  );
}

/** Card grid for the tutorial catalog. */
export function TutorialsGrid({
  mode,
  canEdit,
  canDelete,
  onWatch,
  onEdit,
  onDelete,
}: TutorialsGridProps): React.ReactElement {
  const catalogMode = tutorialCatalogMode(mode);
  const videosQuery = useQuery({
    queryKey: canEdit ? tutorialKeys.admin(catalogMode) : tutorialKeys.catalog(catalogMode),
    queryFn: () => (canEdit ? getTutorialVideosAdmin(catalogMode) : getTutorialVideos(catalogMode)),
    enabled: mode !== null,
  });

  const videos = tutorialRowsVisibleInMode(
    tutorialCatalogRows(videosQuery.data, videosQuery.isError),
    catalogMode,
  );
  const allowRowActions = canEditTutorialRows(canEdit, !!videosQuery.data);

  if (videosQuery.isPending) return <TutorialsGridLoading />;
  if (videos.length === 0) return <TutorialsGridEmpty />;
  return (
    <TutorialsGridCards
      videos={videos}
      canEdit={allowRowActions && canEdit}
      canDelete={allowRowActions && canDelete}
      onWatch={onWatch}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
}
